import type { AppState, CardDeck, GameMode, GameSession, LicenseUnlock, OnboardingData, PenetrationLevel } from "../types";

const keys = {
  onboardingCompleted: "tlc_onboarding_completed",
  husbandName: "tlc_husband_name",
  wifeName: "tlc_wife_name",
  activeSession: "tlc_active_session",
  licenseUnlock: "tlc_license_unlock",
  deviceId: "tlc_device_id"
} as const;

export function storageAvailable(): boolean {
  const storage = getStorage();
  if (!storage) return false;
  try {
    const probe = "__tlc_storage_probe__";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function loadAppState(): AppState {
  const onboardingCompleted = readString(keys.onboardingCompleted) === "true";
  return {
    onboardingCompleted,
    husbandName: readString(keys.husbandName) ?? "",
    wifeName: readString(keys.wifeName) ?? "",
    licenseUnlock: readLicenseUnlock(),
    activeSession: readActiveSession()
  };
}

export function saveOnboarding(data: OnboardingData): void {
  writeString(keys.onboardingCompleted, String(data.onboardingCompleted));
  writeString(keys.husbandName, data.husbandName);
  writeString(keys.wifeName, data.wifeName);
}

export function saveActiveSession(session?: GameSession): void {
  if (!session) {
    removeKey(keys.activeSession);
    return;
  }
  writeJson(keys.activeSession, session);
}

export function ensureDeviceId(): string {
  const existing = readString(keys.deviceId);
  if (existing) return existing;
  const deviceId = createDeviceId();
  writeString(keys.deviceId, deviceId);
  return deviceId;
}

export function saveLicenseUnlock(unlock: LicenseUnlock): void {
  writeJson(keys.licenseUnlock, unlock);
  writeString(keys.deviceId, unlock.deviceId);
}

export function resetOnboarding(): void {
  removeKey(keys.onboardingCompleted);
  removeKey(keys.husbandName);
  removeKey(keys.wifeName);
}

function readString(key: string): string | undefined {
  const storage = getStorage();
  if (!storage) return undefined;
  try {
    return storage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function readJson<T>(key: string): T | undefined {
  const value = readString(key);
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

function readLicenseUnlock(): LicenseUnlock | undefined {
  const unlock = readJson<LicenseUnlock>(keys.licenseUnlock);
  if (!unlock?.licenseKey || !unlock.deviceId || !unlock.unlockToken || !unlock.activatedAt) return undefined;
  return unlock;
}

function readActiveSession(): GameSession | undefined {
  const session = readJson<Partial<GameSession>>(keys.activeSession);
  if (!isValidActiveSession(session)) {
    if (session) removeKey(keys.activeSession);
    return undefined;
  }
  return session;
}

function isValidActiveSession(session: Partial<GameSession> | undefined): session is GameSession {
  if (!session) return false;
  if (!session.id || !session.startedAt || session.status !== "active") return false;
  if (!isGameMode(session.mode) || !isCardDeck(session.currentDeck)) return false;
  if (session.selectedPenetrationLevel && !isPenetrationLevel(session.selectedPenetrationLevel)) return false;
  if (!Array.isArray(session.completedCardIds) || !Array.isArray(session.skippedCardIds)) return false;

  return (
    isNumber(session.talksCompleted) &&
    isNumber(session.foreplayCompleted) &&
    isNumber(session.foreplayHusbandCompleted) &&
    isNumber(session.foreplayWifeCompleted) &&
    isNumber(session.penetrationCompleted) &&
    isNumber(session.totalCompleted)
  );
}

function isGameMode(value: unknown): value is GameMode {
  return value === "talks" || value === "foreplay_husband" || value === "foreplay_wife" || value === "penetration";
}

function isCardDeck(value: unknown): value is CardDeck {
  return isGameMode(value);
}

function isPenetrationLevel(value: unknown): value is PenetrationLevel {
  return value === "normal" || value === "hard" || value === "extreme";
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function writeString(key: string, value: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // The UI shows a storage warning; gameplay still works in memory.
  }
}

function writeJson(key: string, value: unknown): void {
  writeString(key, JSON.stringify(value));
}

function removeKey(key: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // The UI shows a storage warning; gameplay still works in memory.
  }
}

function getStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function createDeviceId(): string {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef?.randomUUID) return cryptoRef.randomUUID();
  if (cryptoRef?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    return `device-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
  }
  return `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
